import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  Clock,
  Funnel,
  ArrowRight,
  ArrowLeft,
  SpinnerGap,
} from '@phosphor-icons/react';
import { fmtUSD } from '../utils/currency';
import {
  REVIEW_QUEUE_STATUS_GROUPS,
  canActOnReviewQueueItem,
  matchesReviewQueueFilter,
} from '../utils/placementStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'DRAFT', style: 'bg-slate-100 text-slate-600' },
  submitted: { label: 'PENDING REVIEW', style: 'bg-orange-100 text-orange-700' },
  pending_lead: { label: 'PENDING REVIEW', style: 'bg-orange-100 text-orange-700' },
  pending_lead_review: { label: 'PENDING REVIEW', style: 'bg-orange-100 text-orange-700' },
  reviewing: { label: 'REVIEWING', style: 'bg-amber-100 text-amber-700' },
  lead_approved: { label: 'PENDING MANAGER', style: 'bg-indigo-100 text-indigo-700' },
  pending_manager: { label: 'PENDING MANAGER', style: 'bg-indigo-100 text-indigo-700' },
  pending_manager_review: { label: 'PENDING MANAGER', style: 'bg-indigo-100 text-indigo-700' },
  manager_approved: { label: 'PENDING SA', style: 'bg-blue-100 text-blue-700' },
  approved: { label: 'APPROVED', style: 'bg-emerald-100 text-emerald-700' },
  sa_approved: { label: 'APPROVED', style: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'REJECTED', style: 'bg-red-100 text-red-700' },
  backed_out: { label: 'BACKED OUT', style: 'bg-rose-100 text-rose-700' },
};

const getStatusBadge = (status) => {
  const cfg = statusConfig[status] || { label: status?.replace(/_/g, ' ').toUpperCase(), style: 'bg-slate-100 text-slate-600' };
  return <Badge className={`${cfg.style} text-[10px]`}>{cfg.label}</Badge>;
};

const getDefaultStatusFilter = ({ isSuperadmin, isCeo, isManager }) => {
  if (isSuperadmin || isCeo) return 'pending_sa';
  if (isManager) return 'pending_manager';
  return 'pending_review';
};

const ReviewQueuePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isManager, isLead, isSuperadmin, isCeo } = useAuth();
  const roleFlags = useMemo(() => ({ isSuperadmin, isCeo, isManager, isLead }), [isSuperadmin, isCeo, isManager, isLead]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [filterInitialized, setFilterInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allPlacements, setAllPlacements] = useState([]);

  useEffect(() => {
    if (!filterInitialized && user) {
      const urlFilter = searchParams.get('filter');
      const validFilters = ['pending_review', 'pending_lead', 'pending_manager', 'pending_sa', 'all', 'draft', 'sa_approved', 'backed_out'];
      if (urlFilter && validFilters.includes(urlFilter)) {
        setStatusFilter(urlFilter);
      } else {
        setStatusFilter(getDefaultStatusFilter({ isSuperadmin, isCeo, isManager }));
      }
      setFilterInitialized(true);
    }
  }, [user, isSuperadmin, isCeo, isManager, filterInitialized, searchParams]);

  useEffect(() => { fetchAllPlacements(); }, []);

  useEffect(() => {
    // Apply client-side status filter (works even if backend lacks status group mapping)
    if (statusFilter === 'all') {
      setPlacements(allPlacements);
    } else {
      setPlacements(allPlacements.filter((p) => matchesReviewQueueFilter(p.status, statusFilter)));
    }
  }, [statusFilter, allPlacements]);

  const fetchAllPlacements = async () => {
    setLoading(true);
    try {
      try {
        const response = await axios.get(`${API_URL}/api/placements/review-queue?limit=500`, { withCredentials: true });
        setAllPlacements(response.data.placements || []);
      } catch {
        const response = await axios.get(`${API_URL}/api/placements?limit=500`, { withCredentials: true });
        setAllPlacements((response.data.placements || []).filter((p) => matchesReviewQueueFilter(p.status, 'pending_review')));
      }
    } catch (error) {
      console.error('Failed to fetch placements:', error);
      setAllPlacements([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacements = fetchAllPlacements;

  const handleQuickApprove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Approve this placement?')) return;
    try {
      await axios.post(`${API_URL}/api/placements/${id}/approve`, {}, { withCredentials: true });
      fetchAllPlacements();
    } catch (error) {
      alert('Failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleQuickReject = async (id, e) => {
    e.stopPropagation();
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axios.post(`${API_URL}/api/placements/${id}/reject?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      fetchAllPlacements();
    } catch (error) {
      alert('Failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredPlacements = placements.filter((p) =>
    p.candidate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.created_by_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statsPlacements = allPlacements;

  const countByFilter = (filterKey) => {
    const group = REVIEW_QUEUE_STATUS_GROUPS[filterKey];
    if (group) {
      return statsPlacements.filter((p) => group.includes(p.status)).length;
    }
    return statsPlacements.filter((p) => p.status === filterKey).length;
  };

  const showSaCard = isSuperadmin || isCeo || isManager;
  const pendingSaCount = countByFilter('pending_sa');
  const saFilterHidesPendingItems = (isSuperadmin || isCeo)
    && pendingSaCount > 0
    && statusFilter !== 'pending_sa'
    && statusFilter !== 'pending_review'
    && statusFilter !== 'all';

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      {saFilterHidesPendingItems && (
        <Card className="border-blue-200 bg-blue-50">
          <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-blue-800">
              <strong>{pendingSaCount}</strong> placement{pendingSaCount !== 1 ? 's' : ''} awaiting your final approval
              (e.g. manager-approved or manager-submitted records).
            </p>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 shrink-0" onClick={() => setStatusFilter('pending_sa')}>
              Show Pending SA
            </Button>
          </div>
        </Card>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Review Queue</h1>
          <p className="text-slate-500 mt-1">
            {filteredPlacements.length} placement{filteredPlacements.length !== 1 ? 's' : ''} shown
            {(isSuperadmin || isCeo) && ' — Full visibility (all teams)'}
            {isManager && !isSuperadmin && !isCeo && ' — Your teams; approve then admin finalizes'}
            {isLead && !isManager && ' — Your team members; approve when no manager is assigned'}
          </p>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className={`grid grid-cols-2 gap-3 ${showSaCard ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {[
          { status: 'pending_manager', label: 'Pending Manager', icon: ArrowRight, color: 'indigo' },
          ...(showSaCard ? [{ status: 'pending_sa', label: 'Pending SA', icon: CheckCircle, color: 'blue' }] : []),
          { status: 'pending_review', label: 'All Pending', icon: Funnel, color: 'amber' },
        ].map(({ status, label, icon: Icon, color }) => (
          <Card key={status}
            className={`border-slate-200 cursor-pointer hover:shadow-sm transition-shadow ${statusFilter === status ? `ring-2 ring-${color}-400` : ''}`}
            onClick={() => setStatusFilter(status)}
            data-testid={`stat-card-${status}`}
          >
            <div className="p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-${color}-50 flex items-center justify-center`}>
                <Icon size={18} className={`text-${color}-600`} />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{countByFilter(status)}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <div className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search candidate or submitter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="review-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-52 h-9" data-testid="review-status-filter">
              <Funnel size={14} className="mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_review">All Pending</SelectItem>
              <SelectItem value="pending_manager">Pending Manager Approval</SelectItem>
              {(isSuperadmin || isCeo || isManager) && <SelectItem value="pending_sa">Pending SA (Final Approval)</SelectItem>}
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="sa_approved">Approved</SelectItem>
              <SelectItem value="backed_out">Backed Out</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Queue Table */}
      <Card className="border-slate-200">
        <div className="p-0">
          {loading ? (
            <div className="p-8 text-center"><SpinnerGap size={28} className="animate-spin text-blue-600 mx-auto" /></div>
          ) : filteredPlacements.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Clock size={40} className="mx-auto text-slate-300 mb-3" />
              <p>No placements in this queue</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="review-queue-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Candidate', 'Submitted By', 'Module', 'Category', 'Margin', 'Status', 'Date', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPlacements.map((p) => {
                    const canApprove = canActOnReviewQueueItem(user, p, roleFlags, 'approve');
                    const canReject = canActOnReviewQueueItem(user, p, roleFlags, 'reject');
                    return (
                    <tr key={p.id} className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => navigate(`/dashboard/placements/${p.id}`)}
                      data-testid={`queue-row-${p.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{p.candidate_name}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{p.created_by_name}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${p.module === 'recruiting' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                          {p.module === 'recruiting' ? 'RECRUITING' : 'SALES'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.category?.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{fmtUSD(p.margin)}</td>
                      <td className="px-4 py-3">{getStatusBadge(p.status)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{p.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        {(canApprove || canReject) && (
                          <div className="flex gap-1 justify-end">
                            {canApprove && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={(e) => handleQuickApprove(p.id, e)} data-testid={`approve-btn-${p.id}`}>
                              <CheckCircle size={16} />
                            </Button>
                            )}
                            {canReject && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => handleQuickReject(p.id, e)} data-testid={`reject-btn-${p.id}`}>
                              <XCircle size={16} />
                            </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ReviewQueuePage;
